<template>
  <transition name="explainer-fade-slide">
    <div v-if="!isDismissed && shouldRender" class="px-2 mt-2">
      <div class="explainer-video-wrapper">
        <Video :src="src" :poster="poster" :loop="loop" :preload="preload" />
        <button class="explainer-dismiss-btn" @click="dismiss" aria-label="Dismiss explainer video">&times;</button>
        <div class="explainer-overlay">
          <slot>{{ overlayText }}</slot>
          &nbsp;
          <Link v-if="shouldShowUnlockLink" href="pricing?utm_source=mb-explainer-video" as="link" class="unlock-link">
            {{ I18n.t("editor.download_menu.unlock") }}
          </Link>
        </div>
      </div>
    </div>
  </transition>
</template>

<script lang="ts" setup>
import { ref, onBeforeMount, inject, computed, nextTick } from "vue";
import { Video, Link } from "prism";
import { useCredits } from "@/composables/credits";

const I18n = inject("I18n");
const { credits, fetchCredits, GENERATE_BACKGROUND_CREDIT_COST, isLoading } = useCredits();

const props = defineProps({
  src: { type: String, required: true },
  poster: { type: String, default: "" },
  overlayText: { type: String, default: "" },
  loop: { type: Boolean, default: true },
  preload: { type: String, default: "auto" },
});

const LOCALSTORAGE_KEY = "explainerVideoDismissed";
const dismissed = ref(false);
const shouldRender = ref(false);

onBeforeMount(async () => {
  dismissed.value = localStorage.getItem(LOCALSTORAGE_KEY) === "true";
  if (!dismissed.value) {
    fetchCredits();
    // Defer rendering by a tiny amount to make it smooth
    await nextTick(() => {
      shouldRender.value = true;
    });
  }
});

const dismiss = () => {
  dismissed.value = true;
  localStorage.setItem(LOCALSTORAGE_KEY, "true");
};

const isDismissed = computed(() => {
  return dismissed.value;
});

const shouldShowUnlockLink = computed(() => {
  return credits.value < GENERATE_BACKGROUND_CREDIT_COST && !isLoading.value;
});
</script>

<style scoped>
.explainer-video-wrapper {
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 0.75rem;
  overflow: hidden;
}

.explainer-overlay {
  width: 100%;
  position: absolute;
  bottom: 0;
  color: #fff;
  padding: 0.5rem 1rem;

  font-weight: 600;
  pointer-events: none;
  z-index: 2;
  text-align: center;
  font-size: 0.8rem;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.1) 33%, rgba(0, 0, 0, 0.65) 100%);
}

.unlock-link {
  color: #fff !important;
  display: inline-block !important;
  pointer-events: auto;
}

.unlock-link:hover,
.unlock-link:focus {
  color: #fff !important;
  text-decoration: underline !important;
}

.unlock-link :deep(svg) {
  display: inline-block;
  width: 1rem;
  height: 1rem;
}

.explainer-dismiss-btn {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}
.explainer-dismiss-btn:hover {
  background: rgba(0, 0, 0, 0.7);
}

@media (max-width: 767px) {
  .explainer-video-wrapper {
    aspect-ratio: 2 / 1; /* Mobile: 2:1 */
  }
}

.explainer-fade-slide-enter-active,
.explainer-fade-slide-leave-active {
  transition: opacity 0.5s, transform 0.5s;
}
.explainer-fade-slide-enter-from,
.explainer-fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}
.explainer-fade-slide-enter-to,
.explainer-fade-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
}
.explainer-fade-slide-leave-active {
  pointer-events: none;
}
</style>
